import React from 'react';
import { Link } from 'react-router-dom';
import s from './LegalPage.module.css';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    num: '01',
    title: 'Acceptance of Terms',
    body: (
      <p>
        By accessing or using the Movement Green website (movementgreen.com.au) or any
        services offered by Movement Green Pty Ltd ("Movement Green", "we", "us"), you agree
        to be bound by these Terms of Use and our{' '}
        <Link to="/privacy">Privacy Policy</Link>. If you do not accept these terms, please
        do not use this website or our services. We reserve the right to modify these terms
        at any time; continued use after changes constitutes acceptance.
      </p>
    ),
  },
  {
    num: '02',
    title: 'Nature of Our Services',
    body: (
      <>
        <p>
          Movement Green Pty Ltd is an Accredited Person (AP) under the Victorian Energy
          Upgrades (VEU) program, accredited by the Essential Services Commission (ESC) of
          Victoria. We help Victorian homeowners and occupiers identify and complete upgrades
          eligible for government rebates. Our services include:
        </p>
        <ul className={s.list}>
          <li>Providing information about VEU-eligible products and upgrades.</li>
          <li>Conducting or arranging free home energy assessments.</li>
          <li>Supplying and installing eligible products, directly or through qualified installers working under our accreditation.</li>
          <li>Creating Victorian Energy Efficiency Certificates (VEECs) and applying the VEU discount to your invoice.</li>
        </ul>
        <p>
          <strong>Movement Green is a VEU Accredited Person (AP).</strong> We manage the
          VEU process end to end, including scheme compliance and certificate creation.
          Please refer to our{' '}
          <Link to="/disclosure">Disclosure</Link> page for full details of our role and
          accreditation.
        </p>
      </>
    ),
  },
  {
    num: '03',
    title: 'Eligibility & Rebate Information',
    body: (
      <>
        <p>
          Information on this website about VEU rebate amounts, eligible products, and
          savings estimates is provided in good faith for general guidance only. Actual rebate
          values depend on factors including the specific product installed, your property
          type, prevailing VEEC prices, and requirements set by the Essential Services
          Commission (ESC) of Victoria.
        </p>
        <p>
          We do not guarantee that any particular upgrade will qualify for a rebate, or that
          any stated saving will be achieved. Eligibility is assessed by Movement Green as the
          Accredited Person in accordance with the rules set by the ESC, and final eligibility
          is confirmed prior to installation.
        </p>
      </>
    ),
  },
  {
    num: '04',
    title: 'No Financial or Energy Advice',
    body: (
      <p>
        Nothing on this website constitutes financial, investment, tax, or professional energy
        advice. Information is provided for general informational purposes only. You should
        obtain independent professional advice before making any decision based on information
        on this website.
      </p>
    ),
  },
  {
    num: '05',
    title: 'Permitted Use',
    body: (
      <>
        <p>
          You may use this website for lawful purposes only. You must not:
        </p>
        <ul className={s.list}>
          <li>Use the site in any way that violates applicable local, state, national, or international laws or regulations.</li>
          <li>Transmit any unsolicited or unauthorised advertising or promotional material (spam).</li>
          <li>Attempt to gain unauthorised access to any part of the website or its connected systems.</li>
          <li>Use automated scripts, bots, or scraping tools to collect information from this website without our prior written consent.</li>
          <li>Reproduce, republish, or redistribute any content from this website without attribution and our express permission.</li>
        </ul>
      </>
    ),
  },
  {
    num: '06',
    title: 'Intellectual Property',
    body: (
      <p>
        All content on this website — including text, graphics, logos, images, and software —
        is the property of Movement Green Pty Ltd or its content suppliers and is protected
        by Australian and international intellectual property laws. You may not copy, modify,
        distribute, or create derivative works from any content without our express written
        consent.
      </p>
    ),
  },
  {
    num: '07',
    title: 'Third-Party Links & Services',
    body: (
      <p>
        This website may contain links to third-party websites, including government portals
        (such as sustainability.vic.gov.au) and partner sites. These links are provided for
        convenience only. Movement Green does not endorse or assume responsibility for the
        content, accuracy, or practices of any third-party site. Accessing third-party sites
        is at your own risk and subject to those sites' terms and privacy policies.
      </p>
    ),
  },
  {
    num: '08',
    title: 'Disclaimer of Warranties',
    body: (
      <>
        <p>
          This website and its content are provided on an "as is" and "as available" basis.
          To the maximum extent permitted by law, Movement Green makes no representations or
          warranties of any kind — express or implied — about:
        </p>
        <ul className={s.list}>
          <li>The accuracy, completeness, reliability, or suitability of any information on the site.</li>
          <li>The uninterrupted or error-free operation of the website.</li>
          <li>The results that may be obtained from using our services.</li>
        </ul>
      </>
    ),
  },
  {
    num: '09',
    title: 'Limitation of Liability',
    body: (
      <p>
        To the fullest extent permitted by the Australian Consumer Law and other applicable
        legislation, Movement Green, its directors, employees, and agents will not be liable
        for any direct, indirect, incidental, special, or consequential loss or damage arising
        from your use of this website or our services, including loss of data, profits, or
        business opportunity. Where statutory guarantees cannot be excluded, our liability
        is limited to re-supplying the relevant service or paying the cost of having it
        re-supplied.
      </p>
    ),
  },
  {
    num: '10',
    title: 'Governing Law',
    body: (
      <p>
        These Terms of Use are governed by and construed in accordance with the laws of
        the State of Victoria, Australia. You irrevocably submit to the non-exclusive
        jurisdiction of the courts of Victoria and courts of appeal therefrom.
      </p>
    ),
  },
  {
    num: '11',
    title: 'Contact',
    body: (
      <p>
        Questions about these Terms of Use should be directed to us at{' '}
        <a href="mailto:contact@movementgreen.com.au">contact@movementgreen.com.au</a>.
      </p>
    ),
  },
];

export default function TermsOfUsePage() {
  return (
    <main className="page-enter">
      <SEO
        title="Terms of Use | Movement Green"
        description="Terms of Use for the Movement Green website and Victorian Energy Upgrade services provided by Movement Green, a VEU Accredited Person."
        noindex
      />

      {/* Hero */}
      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroBadge}>Terms of Use</div>
          <h1 className={`disp-lg ${s.heroTitle}`}>
            Using Movement<br/><em>Green Fairly.</em>
          </h1>
          <p className={s.heroMeta}>Effective date: 30 September 2026 &nbsp;·&nbsp; Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia</p>
        </div>
      </section>

      {/* Body */}
      <section className={s.body}>
        <div className={s.bodyInner}>

          {/* Intro */}
          <div className={s.section}>
            <div className={s.sectionBody}>
              <p>
                These Terms of Use govern your access to and use of the Movement Green website
                and services. Please read them carefully. If you have questions, contact us
                before using our site or services.
              </p>
            </div>
          </div>

          <div className={s.divider}/>

          {SECTIONS.map((sec, i) => (
            <React.Fragment key={sec.num}>
              <div className={s.section}>
                <span className={s.sectionNum}>{sec.num}</span>
                <h2 className={s.sectionTitle}>{sec.title}</h2>
                <div className={s.sectionBody}>{sec.body}</div>
              </div>
              {i < SECTIONS.length - 1 && <div className={s.divider}/>}
            </React.Fragment>
          ))}

          <div className={s.divider}/>

          {/* Contact strip */}
          <div className={s.contact}>
            <div className={s.contactTitle}>Legal Enquiries</div>
            <p className={s.contactBody}>
              For questions about these Terms of Use or our legal obligations, contact us at:
            </p>
            <a href="mailto:contact@movementgreen.com.au" className={s.contactEmail}>
              contact@movementgreen.com.au
            </a>
            <p className={s.contactBody} style={{ marginTop: 8 }}>
              Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia
            </p>
          </div>

          <p className="body-sm" style={{ textAlign: 'center' }}>
            Also see our <Link to="/privacy" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Privacy Policy</Link> and <Link to="/disclosure" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Disclosure</Link>.
          </p>

        </div>
      </section>
    </main>
  );
}
